import React, { useState, createContext, useContext, useEffect, useCallback, Suspense } from 'react';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './services/firebase';
import { getDocument, setDocument } from './services/firestoreService';
import { Book } from 'lucide-react';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/layout/AdminLayout';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import AdminPage from './pages/AdminPage';

const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const JadwalPage = React.lazy(() => import('./pages/JadwalPage'));
const AbsensiPage = React.lazy(() => import('./pages/AbsensiPage'));
const JurnalPage = React.lazy(() => import('./pages/JurnalPage'));
const NilaiPage = React.lazy(() => import('./pages/NilaiPage'));
const SiswaPage = React.lazy(() => import('./pages/SiswaPage'));
const AgendaPage = React.lazy(() => import('./pages/AgendaPage'));
const LaporanPage = React.lazy(() => import('./pages/LaporanPage'));
const PengaturanPage = React.lazy(() => import('./pages/PengaturanPage'));
const BantuanPage = React.lazy(() => import('./pages/BantuanPage'));
const KalenderPendidikanPage = React.lazy(() => import('./pages/KalenderPendidikanPage'));
const SubscribePage = React.lazy(() => import('./pages/SubscribePage'));

export const AuthContext = createContext();
export const AppStateContext = createContext();

const defaultSettings = {
  masterKelas: [],
  masterMapel: [],
  theme: 'light',
  namaGuru: '', nipGuru: '', namaSekolah: '',
  namaKepalaSekolah: '', nipKepalaSekolah: '',
  isPremium: false,
  trialStart: null,
  premiumUntil: null,
};

const adminEmails = [
  'balkhi005@gmail.com', // ganti dengan email admin sebenarnya
];

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { const unsubscribe = onAuthStateChanged(auth, (user) => { setUser(user); setLoading(false); }); return unsubscribe; }, []);
  const login = () => { const provider = new GoogleAuthProvider(); return signInWithPopup(auth, provider); };
  const logout = () => signOut(auth);
  const value = { user, loading, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AppStateProvider = ({ children }) => {
    const { user } = useContext(AuthContext);
    const [lastSession, setLastSession] = useState(null); 
    const [settings, setSettings] = useState(defaultSettings);
    const [agenda, setAgenda] = useState({});
    const [trialInfo, setTrialInfo] = useState({
      isTrialActive: false,
      isPremium: false,
      trialDaysLeft: 0,
      premiumDaysLeft: 0,
    });
    const [isAdmin, setIsAdmin] = useState(false);

    const fetchSettings = useCallback(async (uid) => {
        const settingsPath = `users/${uid}/pengaturan`;
        try {
            let userSettings = await getDocument(settingsPath, 'utama');
            let needUpdate = false;
            if (!userSettings) {
                userSettings = { ...defaultSettings, agenda: {} };
                needUpdate = true;
            }
            if (!userSettings.trialStart) {
                userSettings.trialStart = new Date().toISOString();
                needUpdate = true;
            }
            if (needUpdate) {
                await setDocument(settingsPath, 'utama', userSettings, true);
            }
            const combinedSettings = { ...defaultSettings, ...userSettings };
            setSettings(combinedSettings);
            setAgenda(userSettings.agenda || {});
            document.documentElement.className = combinedSettings.theme || 'light';
            // Hitung status trial/premium
            const today = new Date();
            let isTrialActive = false;
            let isPremium = false;
            let trialDaysLeft = 0;
            let premiumDaysLeft = 0;
            if (combinedSettings.trialStart) {
                const trialStart = new Date(combinedSettings.trialStart);
                const trialEnd = new Date(trialStart);
                trialEnd.setDate(trialEnd.getDate() + 7);
                isTrialActive = today <= trialEnd;
                trialDaysLeft = Math.max(0, Math.ceil((trialEnd - today) / (1000 * 60 * 60 * 24)));
            }
            if (combinedSettings.premiumUntil) {
                const premiumEnd = new Date(combinedSettings.premiumUntil);
                isPremium = today <= premiumEnd;
                premiumDaysLeft = Math.max(0, Math.ceil((premiumEnd - today) / (1000 * 60 * 60 * 24)));
            }
            setTrialInfo({ isTrialActive, isPremium, trialDaysLeft, premiumDaysLeft });
        } catch (error) { console.error("Gagal mengambil pengaturan:", error); }
    }, []);

    useEffect(() => {
      if (user) {
        setIsAdmin(adminEmails.includes(user.email));
        fetchSettings(user.uid);
        // Sync email & displayName ke pengaturan Firestore
        setDocument(`users/${user.uid}/pengaturan`, 'utama', { email: user.email, displayName: user.displayName }, true);
        // Tambahan: update root dokumen user juga
        setDocument('users', user.uid, { email: user.email, displayName: user.displayName }, true);
      } else {
        setIsAdmin(false);
        setSettings(defaultSettings);
        setAgenda({});
      }
    }, [user, fetchSettings]);

    const updateSettings = (newSettingsData) => {
        if (!user) return;
        const updated = { ...settings, ...newSettingsData };
        setSettings(updated);
        const path = `users/${user.uid}/pengaturan`;
        setDocument(path, 'utama', { ...updated, agenda }, true).catch(err => console.error("Gagal update settings:", err));
        // Jika ada perubahan displayName/email, update root user juga
        if (newSettingsData.displayName || newSettingsData.email) {
          setDocument('users', user.uid, {
            ...(newSettingsData.displayName ? { displayName: newSettingsData.displayName } : {}),
            ...(newSettingsData.email ? { email: newSettingsData.email } : {})
          }, true);
        }
    };

    const updateAgenda = (newAgendaData) => {
        if (!user) return;
        setAgenda(newAgendaData);
        const path = `users/${user.uid}/pengaturan`;
        setDocument(path, 'utama', { ...settings, agenda: newAgendaData }, true).catch(err => console.error("Gagal update agenda:", err));
    };

    const value = { lastSession, setLastSession, settings, updateSettings, agenda, updateAgenda, ...trialInfo, isAdmin };
    return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
};

const pageRoutes = [
  { path: '/dashboard', name: 'Dashboard', element: DashboardPage },
  { path: '/jadwal', name: 'Jadwal', element: JadwalPage },
  { path: '/absensi', name: 'Absensi', element: AbsensiPage },
  { path: '/jurnal', name: 'Jurnal', element: JurnalPage },
  { path: '/nilai', name: 'Nilai', element: NilaiPage },
  { path: '/subscribe', name: 'Subscribe', element: SubscribePage },
  { path: '/data-siswa', name: 'Siswa', element: SiswaPage },
  { path: '/agenda', name: 'Agenda', element: AgendaPage },
  { path: '/laporan', name: 'Laporan', element: LaporanPage },
  { path: '/pengaturan', name: 'Pengaturan', element: PengaturanPage },
  { path: '/bantuan', name: 'Bantuan', element: BantuanPage },
  { path: '/kalender-pendidikan', name: 'Kaldik', element: KalenderPendidikanPage },
];

function MainAppRoutes() {
  const { user, loading } = useContext(AuthContext);
  const { isAdmin } = useContext(AppStateContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect otomatis ke /admin jika user admin dan belum di /admin
  useEffect(() => {
    if (user && isAdmin && !location.pathname.startsWith('/admin')) {
      navigate('/admin', { replace: true });
    }
  }, [user, isAdmin, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="p-6 bg-gradient-to-br from-[#8A78F0] to-[#6C52EE] rounded-2xl shadow-2xl">
              <Book size={48} className="text-white"/>
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">LakuKelas</h2>
            <p className="text-gray-600 dark:text-gray-300">Memuat aplikasi...</p>
            <div className="w-32 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#8A78F0] to-[#6C52EE] rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAdmin && user && location.pathname.startsWith('/admin')) {
    return <AdminPage />;
  }

  return (
    <Suspense fallback={<div className="flex justify-center items-center h-full p-8"><div className="w-8 h-8 border-2 border-[#7963F5] border-t-transparent rounded-full animate-spin"></div></div>}>
      <Routes>
        {/* Public routes - TANPA AppLayout */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />

        {/* Private routes - DENGAN AppLayout */}
        {user && (
          <Route element={<AppLayout currentPage={location.pathname.replace(/^\//, '') || 'dashboard'} navigate={to => navigate(to.startsWith('/') ? to : '/' + to)} />}>
            {pageRoutes.map(({ path, element: Element }) => (
              <Route key={path} path={path} element={<Element navigate={to => navigate(to.startsWith('/') ? to : '/' + to)} />} />
            ))}
          </Route>
        )}

        {/* Fallback */}
        <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppStateProvider>
        <Router>
          <MainAppRoutes />
        </Router>
      </AppStateProvider>
    </AuthProvider>
  );
}